import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Button, Table, Tooltip } from "antd";
import ReportsGemstoneService from "../../../../services/reports-Gemstone.service";
import { BlobToDownloadFile } from "../../../../helper/commanFunction";

// Shared searchable/sortable/paginated detail table for the gemstone attribute
// reports. Consolidates the ~10-column antd <Table> + GetGemstoneReportForTableChartAll
// + ExportDataToCSV wiring that was previously duplicated in every *IndComp.js file.
const DetailSearchTable = ({
  dealerId,
  forDealer,
  dataRangeFrom,
  dataRangeTo,
  selectedGemstoneValue,
  reportName,
  filterParamName,
  filterValue,
  fileNamePart,
  dealerCompany,
  refetchToken,
  title,
  reporttype,
  filterViaSearchText,
}) => {
  const { t } = useTranslation();

  const [dataSource, setDataSource] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [paginationPage, setPaginationPage] = useState(1);
  const [sortColumnName, setSortColumnName] = useState("GT.MODIFIED");
  const [sortType, setSortType] = useState("DESC");
  const [searchQuery, setSearchQuery] = useState("");
  const isFirstSearchRender = useRef(true);

  const buildInputData = (pageNumber, size, sortCol, sortOrder, searchText) => ({
    dealerID: dealerId != null ? dealerId.toString() : "",
    forDealer: forDealer ? forDealer.toString() : "",
    fromDate: dataRangeFrom
      ? moment(dataRangeFrom).format("YYYY-MM-DD")
      : "2020-08-17",
    toDate: dataRangeTo
      ? moment(dataRangeTo).format("YYYY-MM-DD")
      : "2022-09-15",
    [filterParamName]: filterValue ? filterValue.toString() : "",
    selectedGemstoneValue: selectedGemstoneValue,
    pageNumber: pageNumber || 1,
    pageSize: size || 15,
    sortOrderCol: sortCol || "GT.MODIFIED",
    sortOrderType: sortOrder || "DESC",
    reportName: reportName,
    reporttype: reporttype,
    // Typed search wins; otherwise fall back to the chart-slice value for
    // reports whose filter column has no dedicated SP param.
    SearchText: searchText || (filterViaSearchText && filterValue ? filterValue.toString() : ""),
  });

  const fetchData = (pageNumber, size, sortCol, sortOrder, searchText) => {
    if (dealerId == null) return;
    setTableLoading(true);
    ReportsGemstoneService.GetGemstoneReportForTableChartAll(
      buildInputData(pageNumber, size, sortCol, sortOrder, searchText)
    )
      .then((response) => {
        setTableLoading(false);
        if (response.data.message === "Success" && response.data.responseData) {
          const responseData = response.data.responseData;
          setTotalCount(responseData.totalCount || 0);
          setDataSource(responseData.modelTableChartAllList || []);
        } else {
          setTotalCount(0);
          setDataSource([]);
        }
      })
      .catch(() => {
        setTableLoading(false);
        setTotalCount(0);
        setDataSource([]);
      });
  };

  // Refetch from page 1 whenever the parent report/filter context changes
  // (chart-slice click, Apply Filter, or the page first becomes ready).
  useEffect(() => {
    setPageNo(1);
    setPaginationPage(1);
    setSearchQuery("");
    fetchData(1, pageSize, sortColumnName, sortType, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId, forDealer, filterValue, refetchToken]);

  // Debounced full-dataset search — searches server-side across every page,
  // not just the currently loaded one (backend @SearchText param).
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    const handle = setTimeout(() => {
      setPageNo(1);
      setPaginationPage(1);
      fetchData(1, pageSize, sortColumnName, sortType, searchQuery.trim());
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleTableChange = (pagination, _filters, sorter) => {
    const tableBody = document.querySelector(".edr-detail-table .ant-table-body");
    if (tableBody) tableBody.scrollTop = 0;

    let type = "DESC";
    let column = "GT.MODIFIED";
    if (sorter.order === "descend") {
      type = "DESC";
      column = sorter.field;
    } else if (sorter.order === "ascend") {
      type = "ASC";
      column = sorter.field;
    }

    setSortType(type);
    setSortColumnName(column);
    setPageNo(pagination.current);
    setPageSize(pagination.pageSize);
    fetchData(pagination.current, pagination.pageSize, column, type, searchQuery.trim());
  };

  const handleExportCSV = () => {
    setCsvLoading(true);
    ReportsGemstoneService.ExportDataToCSV(
      buildInputData(pageNo, pageSize, sortColumnName, "DESC", searchQuery.trim())
    )
      .then((response) => {
        setCsvLoading(false);
        const fileName = `${(dealerCompany || "").replace(/\s+/g, "")}_${fileNamePart}_${new Date()
          .toJSON()
          .slice(0, 10)
          .split("-")
          .slice(1)
          .concat(new Date().toJSON().slice(0, 4))
          .join("_")}.csv`;
        BlobToDownloadFile(response.data, fileName);
      })
      .catch(() => {
        setCsvLoading(false);
      });
  };

  const handleExportPDF = () => {
    setPdfLoading(true);
    ReportsGemstoneService.ExportTableDataToPDF(
      buildInputData(pageNo, pageSize, sortColumnName, "DESC", searchQuery.trim())
    )
      .then((response) => {
        setPdfLoading(false);
        const fileName = `${(dealerCompany || "").replace(/\s+/g, "")}_${fileNamePart}_${new Date()
          .toJSON()
          .slice(0, 10)
          .split("-")
          .slice(1)
          .concat(new Date().toJSON().slice(0, 4))
          .join("_")}.pdf`;
        BlobToDownloadFile(response.data, fileName, "pdf");
      })
      .catch(() => {
        setPdfLoading(false);
      });
  };

  return (
    <div className="edr-panel mt-4">
      <div className="edr-panel-head">
        <div>
          <h3>{title || t("Recent Searches")}</h3>
          <div className="edr-panel-desc">{t("Individual search log · click a column to sort")}</div>
        </div>
        <div className="edr-pagehead-actions">
          <Button className="ghost-btn" onClick={handleExportPDF} loading={pdfLoading}>
            <i className="fa fa-download"></i> {t("PDF")}
          </Button>
          <Button className="ghost-btn" onClick={handleExportCSV} loading={csvLoading}>
            <i className="fa fa-download"></i> {t("CSV")}
          </Button>
        </div>
      </div>

      <div className="edr-tblbar">
        <input
          className="edr-tblsearch"
          placeholder={t("Search all pages: IP, shape, color, clarity…")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <span className="edr-badge">{t("{{count}} rows", { count: totalCount })}</span>
      </div>

      <div className="edr-detail-table">
        <Table
          className="pendjewelreq__tbl"
          scroll={{ x: 1200, y: "calc(100vh - 340px)" }}
          loading={tableLoading}
          columns={[
            { title: <Tooltip title={t("UserIPAddress")}>{t("UserIPAddress")}</Tooltip>, ellipsis: true, dataIndex: "usersIPAddress", sorter: () => {} },
            { title: <Tooltip title={t("Date")}>{t("Date")}</Tooltip>, ellipsis: true, dataIndex: "date", key: "GT.MODIFIED", defaultSortOrder: "descend", sorter: () => {}, render: (item, row) => moment(row.date).format("MM-DD-YYYY, hh:mm:ss A") },
            { title: <Tooltip title={t("Shape")}>{t("Shape")}</Tooltip>, ellipsis: true, dataIndex: "shape", sorter: () => {} },
            { title: <Tooltip title={t("Size")}>{t("Size")}</Tooltip>, ellipsis: true, dataIndex: "size", sorter: () => {} },
            { title: <Tooltip title={t("Color")}>{t("Color")}</Tooltip>, ellipsis: true, dataIndex: "color", sorter: () => {} },
            { title: <Tooltip title={t("Gem Type")}>{t("Gem Type")}</Tooltip>, ellipsis: true, dataIndex: "gemTypeName", sorter: () => {} },
            { title: <Tooltip title={t("Clarity")}>{t("Clarity")}</Tooltip>, ellipsis: true, dataIndex: "clarity", sorter: () => {}, render: (row, item) => item.clarity && <span>{item.clarity}</span> },
            { title: <Tooltip title={t("Certificate")}>{t("Certificate")}</Tooltip>, ellipsis: true, dataIndex: "certificate", sorter: () => {} },
            { title: <Tooltip title={t("RetailerPrice")}>{t("RetailerPrice")}</Tooltip>, ellipsis: true, dataIndex: "retailPrice", sorter: () => {}, render: (row, item) => { const v = item.retailPrice || "Call For Price"; return (<Tooltip title={v}><span>{v}</span></Tooltip>); } },
            { title: <Tooltip title={t("Cost")}>{t("Cost")}</Tooltip>, ellipsis: true, dataIndex: "cost", sorter: () => {}, render: (row, item) => { const v = item.cost || "Call For Price"; return (<Tooltip title={v}><span>{v}</span></Tooltip>); } },
          ]}
          dataSource={dataSource}
          pagination={{
            size: "small",
            total: totalCount,
            pageNo: pageNo,
            pageSize: pageSize,
            pageSizeOptions: [10, 15, 20, 50, 100],
            responsive: true,
            showSizeChanger: true,
            current: paginationPage,
            onChange: (pageNum) => setPaginationPage(pageNum),
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
};

export default DetailSearchTable;
